import { createSlice, PayloadAction, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { Org } from "../schema/types/org/entity";
import { Gvc } from "../schema/types/gvc/entity";
import { Workload } from "../schema/types/workload/entity";
import { Deployment, DeploymentVersion } from "../schema/types/workload/deployment";
import { VolumeSet } from "../schema/types/volumeSet/entity";
import { Domain } from "../schema/types/domain/entity";
import { Quota } from "../schema/types/quota";
import { Agent, AgentInfo } from "../schema/types/agent/entity";
import { linksOf, request } from "../services/cpln";
import { RootState } from ".";
import { workloadHelpers } from "../mst/kinds/workload.helpers";

// Date helpers for UTC month calculations
function startOfMonthUTC(date: Date): Date {
  const d = new Date(date);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function addMonthsUTC(date: Date, months: number): Date {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}

// Billing/Usage types
export interface UsageMetrics {
  projectedMonthlyCost: number;
  actualSpendToDate: number;
  workloadCount: number;
  cpuCores: number;
  cpuMonthlyCost: number;
  ramGib: number;
  ramMonthlyCost: number;
  egressGb: number;
  egressMonthlyCost: number;
  loadBalancerCount: number;
  loadBalancerMonthlyCost: number;
  mk8sCores: number;
  mk8sMonthlyCost: number;
  storageGb: number;
  storageMonthlyCost: number;
  observabilityGb: number;
  observabilityMonthlyCost: number;
  metricsSamplesCount: number;
  metricsSamplesMonthlyCost: number;
}

export type UsageDateRange = "lastMonth" | "thisMonth";

interface HealthState {
  isFetched: boolean;
  isLoading: boolean;
  // Section-specific loading states
  isSummaryLoading: boolean;
  isDomainsLoading: boolean;
  isQuotasLoading: boolean;
  isAgentsLoading: boolean;
  isGvcsLoading: boolean;
  isWorkloadsLoading: boolean;
  orgName: string;
  org: Org | null;
  gvcs: Gvc[];
  workloads: Workload[];
  gvcWorkloadMap: Record<string, Workload[]>;
  deployments: Deployment[];
  workloadDeploymentMap: Record<string, Deployment[]>;
  volumeSets: VolumeSet[];
  gvcVolumeSetMap: Record<string, VolumeSet[]>;
  domains: Domain[];
  quotas: Quota[];
  agents: Agent[];
  agentInfoMap: Record<string, AgentInfo[]>;
  requestTime: number;
  totalRequestCount: number;
  failedRequestCount: number;
  fullfillTime: number;
  // Usage metrics
  usageMetrics: UsageMetrics;
  isUsageLoading: boolean;
  usageError: string;
  usageDateRange: UsageDateRange;
}

const defaultUsageMetrics: UsageMetrics = {
  projectedMonthlyCost: 0,
  actualSpendToDate: 0,
  workloadCount: 0,
  cpuCores: 0,
  cpuMonthlyCost: 0,
  ramGib: 0,
  ramMonthlyCost: 0,
  egressGb: 0,
  egressMonthlyCost: 0,
  loadBalancerCount: 0,
  loadBalancerMonthlyCost: 0,
  mk8sCores: 0,
  mk8sMonthlyCost: 0,
  storageGb: 0,
  storageMonthlyCost: 0,
  observabilityGb: 0,
  observabilityMonthlyCost: 0,
  metricsSamplesCount: 0,
  metricsSamplesMonthlyCost: 0,
};

const initialState: HealthState = {
  isFetched: false,
  isLoading: false,
  // Section-specific loading states
  isSummaryLoading: false,
  isDomainsLoading: false,
  isQuotasLoading: false,
  isAgentsLoading: false,
  isGvcsLoading: false,
  isWorkloadsLoading: false,
  orgName: "",
  org: null,
  gvcs: [],
  workloads: [],
  gvcWorkloadMap: {},
  deployments: [],
  workloadDeploymentMap: {},
  volumeSets: [],
  gvcVolumeSetMap: {},
  domains: [],
  quotas: [],
  agents: [],
  agentInfoMap: {},
  requestTime: 0,
  totalRequestCount: 0,
  failedRequestCount: 0,
  fullfillTime: 0,
  usageMetrics: defaultUsageMetrics,
  isUsageLoading: false,
  usageError: "",
  usageDateRange: "thisMonth",
};

// silent: true means polling refresh - don't show loading scaffolds
export const fetchHealth = createAsyncThunk("health/fetch", async (options: { silent?: boolean } | undefined, { getState, dispatch }) => {
  const silent = options?.silent ?? false;
  const timeStart = new Date().getTime();
  dispatch(setTotalRequest(0));
  dispatch(setFailedRequest(0));

  // Set all section loading states to true at start (only for initial load, not polling)
  if (!silent) {
    dispatch(setSectionLoading({ section: "summary", loading: true }));
    dispatch(setSectionLoading({ section: "domains", loading: true }));
    dispatch(setSectionLoading({ section: "quotas", loading: true }));
    dispatch(setSectionLoading({ section: "agents", loading: true }));
    dispatch(setSectionLoading({ section: "gvcs", loading: true }));
    dispatch(setSectionLoading({ section: "workloads", loading: true }));
  }

  const state = getState() as RootState;
  const orgName = state.context.org;
  dispatch(setOrgName(orgName));

  // org // gvc // domain // quota // agent
  let promises = [
    dispatch(fetchOrg(orgName)).unwrap(),
    dispatch(fetchAllGvcs(orgName)).unwrap(),
    dispatch(fetchAllDomains(orgName)).unwrap(),
    dispatch(fetchAllQuotas(orgName)).unwrap(),
    dispatch(fetchAllAgents(orgName)).unwrap(),
  ];

  let responses = await Promise.allSettled(promises);
  const orgResponse = responses[0];
  const gvcsResponse = responses[1];
  const domainsResponse = responses[2];
  const quotasResponse = responses[3];
  const agentsResponse = responses[4];

  if (orgResponse.status === "fulfilled") {
    dispatch(setOrg(orgResponse.value.data));
  }
  let gvcs: Gvc[] = [];
  if (gvcsResponse.status === "fulfilled") {
    gvcs = gvcsResponse.value;
    dispatch(setGvcs(gvcsResponse.value));
  }
  if (domainsResponse.status === "fulfilled") {
    dispatch(setDomains(domainsResponse.value));
  }
  if (quotasResponse.status === "fulfilled") {
    dispatch(setQuotas(quotasResponse.value));
  }
  let agents: Agent[] = [];
  if (agentsResponse.status === "fulfilled") {
    dispatch(setAgents(agentsResponse.value));
    agents = agentsResponse.value;
  }

  // Phase 1 complete: domains, quotas, gvcs fetched - summary cards can now show basic counts
  if (!silent) {
    dispatch(setSectionLoading({ section: "summary", loading: false }));
    dispatch(setSectionLoading({ section: "domains", loading: false }));
    dispatch(setSectionLoading({ section: "quotas", loading: false }));
    dispatch(setSectionLoading({ section: "gvcs", loading: false }));
  }

  // agent infos
  // workloads of gvcs
  // volumesets of gvcs
  promises = [
    ...agents.map((agent) => dispatch(fetchAgentInfo({ orgName: orgName, agentName: agent.name })).unwrap()),
    ...gvcs.map((gvc) => dispatch(fetchAllWorkloads({ orgName: orgName, gvcName: gvc.name })).unwrap()),
    ...gvcs.map((gvc) => dispatch(fetchAllVolumeSets({ orgName: orgName, gvcName: gvc.name })).unwrap()),
  ];
  responses = await Promise.allSettled(promises);

  // Get current state's agentInfoMap to preserve existing entries on fetch failure
  const currentState = getState() as RootState;
  const agentInfoMap: Record<string, AgentInfo[]> = { ...currentState.health.agentInfoMap };
  const agentSlice = responses.slice(0, agents.length);
  for (const indexStr in agentSlice) {
    const index = Number(indexStr);
    const response = agentSlice[index];
    if (response.status === "rejected") {
      continue; // Keep existing entry if fetch failed
    }

    const agent = agents[index];
    if (!response.value) {
      // agent info returns null - keep existing entry
      continue;
    }

    agentInfoMap[agent.name] = response.value; // Update with new data
  }
  dispatch(setAgentInfoMap(agentInfoMap));

  // Phase 2 complete: agent info fetched
  if (!silent) {
    dispatch(setSectionLoading({ section: "agents", loading: false }));
  }

  let type: "workload" | "volumeset" = "workload";

  let workloads: Workload[] = [];
  const gvcWorkloadMap: Record<string, Workload[]> = {};
  let volumeSets: VolumeSet[] = [];
  const gvcVolumeSetMap: Record<string, VolumeSet[]> = {};
  const gvcSlice = responses.slice(agents.length);
  for (const indexStr in gvcSlice) {
    const index = Number(indexStr);
    type = "workload";
    if (index >= gvcs.length && type === "workload") {
      type = "volumeset";
    }

    const response = gvcSlice[index];
    if (response.status === "rejected") {
      continue;
    }

    const realIndex = index % gvcs.length;
    const gvc = gvcs[realIndex];

    if (type === "workload") {
      workloads = workloads.concat(response.value);
      gvcWorkloadMap[gvc.name] = response.value;
    }

    if (type === "volumeset") {
      volumeSets = volumeSets.concat(response.value);
      gvcVolumeSetMap[gvc.name] = response.value;
    }
  }

  dispatch(setWorkloads(workloads));
  dispatch(setGvcWorkloadMap(gvcWorkloadMap));
  dispatch(setVolumeSets(volumeSets));
  dispatch(setGvcVolumeSetMap(gvcVolumeSetMap));

  // -----
  // deployments of workloads
  promises = [
    ...workloads.map((w) => {
      const selfLink = linksOf(w).self!;
      return dispatch(fetchAllDeployments(selfLink)).unwrap();
    }),
  ];
  responses = await Promise.allSettled(promises);

  let deployments: Deployment[] = [];
  let workloadDeploymentMap: Record<string, Deployment[]> = {};
  for (const indexStr in responses) {
    const index = Number(indexStr);
    const response = responses[index];
    if (response.status === "rejected") {
      continue;
    }

    deployments = deployments.concat(response.value);
    const workload = workloads[index];
    const workloadLink = linksOf(workload).self!;
    workloadDeploymentMap[workloadLink] = response.value;
  }
  dispatch(setDeployments(deployments));
  dispatch(setWorkloadDeploymentMap(workloadDeploymentMap));

  // Phase 3 complete: deployments fetched, workloads ready
  if (!silent) {
    dispatch(setSectionLoading({ section: "workloads", loading: false }));
  }

  // commands of workloads
  // commands of volumesets

  const timeEnd = new Date().getTime();
  const timeDiff = timeEnd - timeStart;
  dispatch(setRequestTime(timeDiff));
});

export const processHealthOrgChange = createAsyncThunk(
  "health/processHealthOrgChange",
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    if (state.context.org === state.health.orgName) {
      return;
    }

    dispatch(initialize());
    dispatch(fetchHealth());
  },
);

export const fetchOrg = createAsyncThunk("health/fetchOrg", async (orgName: string, { getState, dispatch }) => {
  dispatch(incrementTotalRequest());
  const { data } = await request({ method: "get", url: `/org/${orgName}` });
  return data;
});

export const fetchAllGvcs = createAsyncThunk("health/fetchAllGvcs", async (orgName: string, { getState, dispatch }) => {
  let items: Gvc[] = [];
  let nextLink: string | undefined = `/org/${orgName}/gvc`;
  while (nextLink) {
    dispatch(incrementTotalRequest());
    const { data } = await request({ method: "get", url: nextLink });
    items = items.concat(data.items);
    nextLink = linksOf(data).next;
  }
  return items;
});

export const fetchAllDomains = createAsyncThunk(
  "health/fetchAllDomains",
  async (orgName: string, { getState, dispatch }) => {
    let items: Domain[] = [];
    let nextLink: string | undefined = `/org/${orgName}/domain`;
    while (nextLink) {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: nextLink });
      items = items.concat(data.items);
      nextLink = linksOf(data).next;
    }
    return items;
  },
);

export const fetchAllQuotas = createAsyncThunk(
  "health/fetchAllQuotas",
  async (orgName: string, { getState, dispatch }) => {
    let items: Domain[] = [];
    let nextLink: string | undefined = `/org/${orgName}/quota`;
    while (nextLink) {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: nextLink });
      items = items.concat(data.items);
      nextLink = linksOf(data).next;
    }
    return items;
  },
);

export const fetchAllAgents = createAsyncThunk(
  "health/fetchAllAgents",
  async (orgName: string, { getState, dispatch }) => {
    let items: Domain[] = [];
    let nextLink: string | undefined = `/org/${orgName}/agent`;
    while (nextLink) {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: nextLink });
      items = items.concat(data.items);
      nextLink = linksOf(data).next;
    }
    return items;
  },
);

export const fetchAllWorkloads = createAsyncThunk(
  "health/fetchAllWorkloads",
  async ({ orgName, gvcName }: { orgName: string; gvcName: string }, { getState, dispatch }) => {
    let items: Workload[] = [];
    let nextLink: string | undefined = `/org/${orgName}/gvc/${gvcName}/workload`;
    while (nextLink) {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: nextLink });
      items = items.concat(data.items);
      nextLink = linksOf(data).next;
    }
    return items;
  },
);

export const fetchAllVolumeSets = createAsyncThunk(
  "health/fetchAllVolumeSets",
  async ({ orgName, gvcName }: { orgName: string; gvcName: string }, { getState, dispatch }) => {
    let items: VolumeSet[] = [];
    let nextLink: string | undefined = `/org/${orgName}/gvc/${gvcName}/volumeset`;
    while (nextLink) {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: nextLink });
      items = items.concat(data.items);
      nextLink = linksOf(data).next;
    }
    return items;
  },
);

export const fetchAllDeployments = createAsyncThunk(
  "health/fetchAllDeployments",
  async (link: string, { getState, dispatch }) => {
    let items: Deployment[] = [];
    let nextLink: string | undefined = `${link}/deployment`;
    while (nextLink) {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: nextLink });
      items = items.concat(data.items);
      nextLink = linksOf(data).next;
    }
    return items;
  },
);

export const fetchAgentInfo = createAsyncThunk(
  "health/fetchAgentInfo",
  async ({ orgName, agentName }: { orgName: string; agentName: string }, { getState, dispatch }) => {
    try {
      dispatch(incrementTotalRequest());
      const { data } = await request({ method: "get", url: `/org/${orgName}/agent/${agentName}/-info` });
      return data;
    } catch (e) {
      // failedRequestTickerRef.current += 1;
      return null;
    }
  },
);

// Metric names from API (consumptionTags.metric values)
const CHARGEABLE_ITEM_NAMES = {
  cpu: "cpu",
  memory: "memory",
  egress: "egress",
  loadBalancer: "load-balancer-seconds",
  mk8sCores: "cores-under-management", // mk8s-cores-under-management chargeable item
  storage: "volume_set_capacity", // All storage types share this metric
  // Observability metrics
  metricsSamples: "metrics-samples",
  logsStorage: "logs-storage",
  metricsStorage: "metrics-storage",
  tracingStorage: "tracing-storage",
};

// BYOK chargeable items to exclude from billing metrics
const BYOK_CHARGEABLE_ITEMS = [
  "load-balancers-byok",
  "cpu-byok",
  "memory-byok",
  "egress-byok",
];

// Helper to calculate date range based on selection
function getDateRangeForUsage(dateRange: UsageDateRange): { startTime: string; endTime: string } {
  const now = new Date();

  switch (dateRange) {
    case "lastMonth": {
      const lastMonth = addMonthsUTC(startOfMonthUTC(now), -1);
      return {
        startTime: lastMonth.toISOString(),
        endTime: startOfMonthUTC(now).toISOString(),
      };
    }
    case "thisMonth":
    default: {
      return {
        startTime: startOfMonthUTC(now).toISOString(),
        endTime: addMonthsUTC(startOfMonthUTC(now), 1).toISOString(),
      };
    }
  }
}

export const fetchUsageMetrics = createAsyncThunk(
  "health/fetchUsageMetrics",
  async ({ orgName, dateRange }: { orgName: string; dateRange: UsageDateRange }, { getState }) => {
    const metrics: UsageMetrics = { ...defaultUsageMetrics };

    try {
      // Get time range based on selection
      const { startTime, endTime } = getDateRangeForUsage(dateRange);

      // Fetch all chargeable items first
      const { data: chargeableItems } = await request({
        service: "billing-ng",
        method: "get",
        url: `/chargeable_items`,
      });

      // Build chargeable item names list, excluding BYOK items
      const chargeableItemNames = chargeableItems
        .filter((ci: any) => !BYOK_CHARGEABLE_ITEMS.includes(ci.name))
        .map((ci: any) => ci.name);

      // Fetch cost data
      const mainQuery = {
        id: "main",
        aggregateBy: ["metric"],
        filterBy: {},
        chargeableItems: chargeableItemNames,
      };
      const costRequestBody: any = {
        detailed: false,
        startTime,
        endTime,
        aggregateByTimeStep: true,
        timeStep: "month",
        groupBy: ["metric"],
        mainConsumptionQuery: mainQuery,
        consumptionQueries: [],
      };

      // Match usage page pattern: push main query to consumptionQueries, then delete mainConsumptionQuery
      costRequestBody.consumptionQueries.push(costRequestBody.mainConsumptionQuery);
      for (const cq of costRequestBody.consumptionQueries) {
        delete cq.id;
      }
      delete costRequestBody.mainConsumptionQuery;

      const { data: costData } = await request({
        service: "billing-ng",
        method: "post",
        url: `/org/${orgName}/charges/-query`,
        body: costRequestBody,
      });

      // Process cost data
      let totalProjectedCost = 0;
      let totalActualCost = 0;
      const metricCosts: Record<string, { cost: number; projected: number; value: number; projectedValue: number }> = {};

      if (costData.results?.periods && costData.results.periods.length > 0) {
        const period = costData.results.periods[0];
        for (const group of period.groups || []) {
          // Metric name is in group.key.metric, not consumption.tags
          const metricName = group.key?.metric || "";
          for (const consumption of group.consumptions || []) {
            const cost = consumption.total || 0;
            const projected = consumption.projectedTotal || cost;
            const value = consumption.value || 0;
            const projectedValue = consumption.projectedValue || value;

            totalProjectedCost += projected;
            totalActualCost += cost;

            if (!metricCosts[metricName]) {
              metricCosts[metricName] = { cost: 0, projected: 0, value: 0, projectedValue: 0 };
            }
            metricCosts[metricName].cost += cost;
            metricCosts[metricName].projected += projected;
            metricCosts[metricName].value += value;
            metricCosts[metricName].projectedValue += projectedValue;
          }
        }
      }

      metrics.projectedMonthlyCost = totalProjectedCost;
      metrics.actualSpendToDate = totalActualCost;

      // Extract individual metrics
      const period = costData.results?.periods?.[0];
      // 30 days in seconds as fallback when billing API doesn't return period data
      const MONTH_SECONDS = 30 * 24 * 60 * 60; // 2,592,000
      const elapsedSeconds = period?.elapsedSeconds || period?.totalSeconds || MONTH_SECONDS;

      const cpuMetric = metricCosts[CHARGEABLE_ITEM_NAMES.cpu];
      if (cpuMetric) {
        // CPU value is in cores * seconds, convert to average cores
        metrics.cpuCores = cpuMetric.value / elapsedSeconds;
        metrics.cpuMonthlyCost = cpuMetric.projected;
      }

      const memoryMetric = metricCosts[CHARGEABLE_ITEM_NAMES.memory];
      if (memoryMetric) {
        // Memory value is in bytes * seconds, convert to average GiB
        const gibDivider = 1_073_741_824;
        metrics.ramGib = memoryMetric.value / elapsedSeconds / gibDivider;
        metrics.ramMonthlyCost = memoryMetric.projected;
      }

      const egressMetric = metricCosts[CHARGEABLE_ITEM_NAMES.egress];
      if (egressMetric) {
        // Egress is in bytes, convert to GB
        const gbDivider = 1_000_000_000;
        metrics.egressGb = egressMetric.projectedValue / gbDivider;
        metrics.egressMonthlyCost = egressMetric.projected;
      }

      const lbMetric = metricCosts[CHARGEABLE_ITEM_NAMES.loadBalancer];
      if (lbMetric) {
        // Load balancer value is in LB * seconds, convert to count
        metrics.loadBalancerCount = Math.round(lbMetric.value / elapsedSeconds);
        metrics.loadBalancerMonthlyCost = lbMetric.projected;
      }

      const mk8sMetric = metricCosts[CHARGEABLE_ITEM_NAMES.mk8sCores];
      if (mk8sMetric) {
        // mk8s cores value is in cores * seconds, convert to average cores
        metrics.mk8sCores = mk8sMetric.value / elapsedSeconds;
        metrics.mk8sMonthlyCost = mk8sMetric.projected;
      }

      // Storage - value is in GB * Seconds, convert to average GB
      const storageMetric = metricCosts[CHARGEABLE_ITEM_NAMES.storage];
      if (storageMetric) {
        metrics.storageGb = storageMetric.value / elapsedSeconds;
        metrics.storageMonthlyCost = storageMetric.projected;
      }

      // Metrics Samples - separate from observability
      const metricsSamples = metricCosts[CHARGEABLE_ITEM_NAMES.metricsSamples];
      metrics.metricsSamplesCount = metricsSamples?.projectedValue || 0;
      metrics.metricsSamplesMonthlyCost = metricsSamples?.projected || 0;

      // Observability - sum storage metric costs (logs, metrics, tracing) - NOT including metrics-samples
      const observabilityStorageMetrics = [
        metricCosts[CHARGEABLE_ITEM_NAMES.logsStorage],
        metricCosts[CHARGEABLE_ITEM_NAMES.metricsStorage],
        metricCosts[CHARGEABLE_ITEM_NAMES.tracingStorage],
      ];
      metrics.observabilityMonthlyCost = observabilityStorageMetrics.reduce(
        (sum, metric) => sum + (metric?.projected || 0),
        0,
      );
      // Storage metrics (logs, metrics, tracing) use GB * Seconds
      metrics.observabilityGb = observabilityStorageMetrics.reduce(
        (sum, metric) => sum + (metric?.value || 0) / elapsedSeconds,
        0,
      );

      // Fetch workload count from metering service
      try {
        const workloadTagsBody = {
          startTime,
          endTime,
          tag: "name",
          filterBy: { feature: "workloads" },
        };
        const { data: workloadTags } = await request({
          service: "metering",
          method: "post",
          url: `/org/${orgName}/tags/values/query`,
          body: workloadTagsBody,
        });
        metrics.workloadCount = workloadTags.tagValues?.length || 0;
      } catch (e) {
        // Metering service might not be available, use fallback from state
        console.warn("Failed to fetch workload count from metering service, using state data", e);
        const state = getState() as RootState;
        metrics.workloadCount = state.health.workloads.length;
      }

      return metrics;
    } catch (e) {
      console.error("Failed to fetch usage metrics", e);
      throw e;
    }
  },
);

export const healthSlice = createSlice({
  name: "health",
  initialState,
  reducers: {
    setRequestTime(state, action: PayloadAction<number>) {
      state.requestTime = action.payload;
    },
    setTotalRequest(state, action: PayloadAction<number>) {
      state.totalRequestCount = action.payload;
    },
    incrementTotalRequest(state) {
      state.totalRequestCount += 1;
    },
    setFailedRequest(state, action: PayloadAction<number>) {
      state.failedRequestCount = action.payload;
    },
    incrementFailedRequest(state) {
      state.failedRequestCount += 1;
    },
    setOrgName(state, action: PayloadAction<string>) {
      state.orgName = action.payload;
    },
    setOrg(state, action: PayloadAction<Org | null>) {
      state.org = action.payload;
    },
    setGvcs(state, action: PayloadAction<Gvc[]>) {
      state.gvcs = action.payload;
    },
    setDomains(state, action: PayloadAction<Domain[]>) {
      state.domains = action.payload;
    },
    setQuotas(state, action: PayloadAction<Quota[]>) {
      state.quotas = action.payload;
    },
    setAgents(state, action: PayloadAction<Agent[]>) {
      state.agents = action.payload;
    },
    setAgentInfoMap(state, action: PayloadAction<Record<string, AgentInfo[]>>) {
      state.agentInfoMap = action.payload;
    },
    setWorkloads(state, action: PayloadAction<Workload[]>) {
      state.workloads = action.payload;
    },
    setGvcWorkloadMap(state, action: PayloadAction<Record<string, Workload[]>>) {
      state.gvcWorkloadMap = action.payload;
    },
    setVolumeSets(state, action: PayloadAction<VolumeSet[]>) {
      state.volumeSets = action.payload;
    },
    setGvcVolumeSetMap(state, action: PayloadAction<Record<string, VolumeSet[]>>) {
      state.gvcVolumeSetMap = action.payload;
    },
    setDeployments(state, action: PayloadAction<Deployment[]>) {
      state.deployments = action.payload;
    },
    setWorkloadDeploymentMap(state, action: PayloadAction<Record<string, Deployment[]>>) {
      state.workloadDeploymentMap = action.payload;
    },
    setUsageMetrics(state, action: PayloadAction<UsageMetrics>) {
      state.usageMetrics = action.payload;
    },
    setUsageDateRange(state, action: PayloadAction<UsageDateRange>) {
      state.usageDateRange = action.payload;
    },
    setSectionLoading(
      state,
      action: PayloadAction<{
        section: "summary" | "domains" | "quotas" | "agents" | "gvcs" | "workloads";
        loading: boolean;
      }>,
    ) {
      const { section, loading } = action.payload;
      switch (section) {
        case "summary":
          state.isSummaryLoading = loading;
          break;
        case "domains":
          state.isDomainsLoading = loading;
          break;
        case "quotas":
          state.isQuotasLoading = loading;
          break;
        case "agents":
          state.isAgentsLoading = loading;
          break;
        case "gvcs":
          state.isGvcsLoading = loading;
          break;
        case "workloads":
          state.isWorkloadsLoading = loading;
          break;
      }
    },
    initialize() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch account summaries states
      .addCase(fetchHealth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchHealth.fulfilled, (state) => {
        state.isLoading = false;
        state.isFetched = true;
        state.fullfillTime = new Date().getTime();
      })
      .addCase(fetchHealth.rejected, (state) => {
        state.isLoading = false;
        // TODO can improve?
      })
      // Handle fetch usage metrics states
      .addCase(fetchUsageMetrics.pending, (state) => {
        state.isUsageLoading = true;
        state.usageError = "";
      })
      .addCase(fetchUsageMetrics.fulfilled, (state, action) => {
        state.isUsageLoading = false;
        state.usageMetrics = action.payload;
      })
      .addCase(fetchUsageMetrics.rejected, (state, action) => {
        state.isUsageLoading = false;
        state.usageError = action.error.message || "Failed to fetch usage metrics";
      });
  },
});

export const {
  setRequestTime,
  setTotalRequest,
  incrementTotalRequest,
  setFailedRequest,
  incrementFailedRequest,
  setOrgName,
  setOrg,
  setGvcs,
  setDomains,
  setQuotas,
  setAgents,
  setAgentInfoMap,
  setWorkloads,
  setGvcWorkloadMap,
  setVolumeSets,
  setGvcVolumeSetMap,
  setDeployments,
  setWorkloadDeploymentMap,
  setUsageMetrics,
  setUsageDateRange,
  setSectionLoading,
  initialize,
} = healthSlice.actions;

export const healthReducer = healthSlice.reducer;

// Usage metrics selectors
export const selectUsageMetrics = (state: RootState) => state.health.usageMetrics;
export const selectIsUsageLoading = (state: RootState) => state.health.isUsageLoading;
export const selectUsageError = (state: RootState) => state.health.usageError;
export const selectUsageDateRange = (state: RootState) => state.health.usageDateRange;

// Section loading selectors
export const selectIsSummaryLoading = (state: RootState) => state.health.isSummaryLoading;
export const selectIsDomainsLoading = (state: RootState) => state.health.isDomainsLoading;
export const selectIsQuotasLoading = (state: RootState) => state.health.isQuotasLoading;
export const selectIsAgentsLoading = (state: RootState) => state.health.isAgentsLoading;
export const selectIsGvcsLoading = (state: RootState) => state.health.isGvcsLoading;
export const selectIsWorkloadsLoading = (state: RootState) => state.health.isWorkloadsLoading;

export const selectDomains = (state: RootState) => state.health.domains;
export const selectProcessedDomains = createSelector([selectDomains], (domains) => {
  let processedDomains: { name: string; error: boolean; warning: boolean; status: string; text: string }[] = [];
  for (const domain of domains) {
    const status = domain.status?.status;
    if (!status) {
      continue;
    }
    // Only "errored" and "warning" statuses indicate actual problems
    // All other statuses (ready, initializing, pendingDnsConfig, pendingCertificate,
    // usedByGvc, created, updated, deleted, ignored) are informational/intermediate
    // and should not be shown as issues
    if (status === "ready") {
      continue;
    }
    if (status === "errored") {
      processedDomains.push({
        name: domain.name,
        error: true,
        warning: false,
        status: status || "",
        text: domain.status?.warning || "",
      });
      continue;
    }
    if (status === "warning") {
      processedDomains.push({
        name: domain.name,
        error: false,
        warning: true,
        status: status || "",
        text: domain.status?.warning || "",
      });
      continue;
    }
    // All other statuses are intermediate/informational - don't add to issues list
  }
  return processedDomains;
});

export const selectQuotas = (state: RootState) => state.health.quotas;
export const selectProcessedQuotas = createSelector([selectQuotas], (quotas) => {
  let processedQuotas: {
    name: string;
    description: string;
    gvc: string;
    remaining: number;
    error: boolean;
    warning: boolean;
  }[] = [];
  for (const quota of quotas) {
    if (quota.max === undefined) {
      continue;
    }
    const remaining = quota.max - (quota.current || 0);
    if (remaining > 3) {
      continue;
    }
    let gvc = quota.dimensions?.gvcLink || "";
    if (remaining <= 0) {
      // At limit - no more available space
      processedQuotas.push({
        name: quota.name,
        description: quota.description,
        gvc: gvc,
        remaining: remaining,
        error: true,
        warning: false,
      });
      continue;
    }
    // Near limit - some space remaining (1-3)
    processedQuotas.push({
      name: quota.name,
      description: quota.description,
      gvc: gvc,
      remaining: remaining,
      error: false,
      warning: true,
    });
  }
  return processedQuotas;
});
export const selectGvcs = (state: RootState) => state.health.gvcs;
export const selectWorkloads = (state: RootState) => state.health.workloads;
export const selectDeployments = (state: RootState) => state.health.deployments;
export const selectGvcWorkloadMap = (state: RootState) => state.health.gvcWorkloadMap;
export const selectWorkloadDeploymentMap = (state: RootState) => state.health.workloadDeploymentMap;

// TODO ignore cron
// TODO ignore min scale 0
// This workload location is deactivated because maxScale is set to 0.
// TODO separately check cron health

interface ProcessedWorkloadMessage {
  type: "workload" | "deployment" | "version" | "container";
  location: string;
  version?: number;
  container: string;
  text: string;
}

export interface ProcessedWorkload {
  item: Workload;
  selfLink: string;
  deployments: Deployment[];
  version: number;
  expectedDeploymentVersion: number;
  readyReplicaCount: number;
  isCron: boolean;
  status: {
    isReady: boolean;
    isLatestReady: boolean;
    syncFailed: boolean;
    suspended: boolean;
    partiallySuspended: boolean;
  };
  messages: ProcessedWorkloadMessage[];
}

interface ProcessedGvc {
  item: Gvc;
  workloads: {
    notReady: ProcessedWorkload[];
    notLatest: ProcessedWorkload[];
    syncFailed: ProcessedWorkload[];
    partiallySuspended: ProcessedWorkload[];
    suspended: ProcessedWorkload[];
    ready: ProcessedWorkload[];
  };
}

export const selectProcessedGvcs = createSelector(
  [selectGvcs, selectGvcWorkloadMap, selectWorkloadDeploymentMap],
  (gvcs, gvcWorkloadMap, workloadDeploymentMap) => {
    let processedGvcs: ProcessedGvc[] = [];

    for (const gvc of gvcs) {
      const processedGvc: ProcessedGvc = {
        item: gvc,
        workloads: {
          notReady: [],
          notLatest: [],
          syncFailed: [],
          partiallySuspended: [],
          suspended: [],
          ready: [],
        },
      };

      for (const workload of gvcWorkloadMap[gvc.name] || []) {
        const selfLink = linksOf(workload).self!;
        const deployments = workloadDeploymentMap[selfLink] || [];

        const health = workloadHelpers.getHealth(deployments, workload);
        const suspendType = workloadHelpers.getSuspendType(workload);
        const readyReplicaCount = workloadHelpers.getReadyReplicasOnly(deployments, workload);

        const messages: ProcessedWorkloadMessage[] = [];

        const SUSPEND_MESSAGE = `This workload location is deactivated because suspend is set to true`;
        const MAXSCALE_ZERO_MESSAGE = `This workload location is deactivated because maxScale is set to 0`;

        // Track deployments that are intentionally deactivated (maxScale=0)
        let maxScaleZeroDeploymentCount = 0;

        let expectedDeploymentVersion = 0;
        for (const deployment of deployments) {
          expectedDeploymentVersion = deployment.status?.expectedDeploymentVersion || workload.version;

          let validVersion: DeploymentVersion | null = null;
          for (const [versionKey, versionValue] of Object.entries(deployment.status?.versions || {})) {
            if (!versionValue.workload) {
              continue;
            }

            if (versionValue.workload >= expectedDeploymentVersion) {
              if (!validVersion) {
                validVersion = versionValue;
                continue;
              }

              if (validVersion.workload! < versionValue.workload!) {
                validVersion = versionValue;
              }
            }
          }

          // Check if this deployment has only one version with maxScale=0 message
          // This means the location is intentionally deactivated
          const versions = Object.values(deployment.status?.versions || {});
          const hasOnlyOneVersion = versions.length === 1;

          // Check deployment/version/container messages for maxScale=0
          const deploymentHasMaxScaleZero =
            validVersion?.message?.includes(MAXSCALE_ZERO_MESSAGE) ||
            deployment.status?.message?.includes(MAXSCALE_ZERO_MESSAGE) ||
            Object.values(validVersion?.containers || {}).some((c) => c?.message?.includes(MAXSCALE_ZERO_MESSAGE));

          if (hasOnlyOneVersion && deploymentHasMaxScaleZero) {
            maxScaleZeroDeploymentCount++;
            // Skip collecting messages for this deployment - it's intentionally deactivated
            continue;
          }

          // container level
          if (validVersion) {
            for (const [containerKey, containerValue] of Object.entries(validVersion.containers || {})) {
              if (containerValue && containerValue.message) {
                if (messages.some((m) => m.type === "container")) {
                  continue;
                }
                if (containerValue.message.includes(SUSPEND_MESSAGE) || containerValue.message.includes(MAXSCALE_ZERO_MESSAGE)) {
                  continue;
                }
                messages.push({
                  type: "container",
                  location: deployment.name,
                  version: validVersion.workload,
                  container: containerKey,
                  text: containerValue.message,
                });
                continue;
              }
            }
          }

          // version level
          if (validVersion && validVersion.message) {
            if (!messages.some((m) => m.type === "version") && !validVersion.message.includes(SUSPEND_MESSAGE) && !validVersion.message.includes(MAXSCALE_ZERO_MESSAGE)) {
              messages.push({
                type: "version",
                location: deployment.name,
                version: validVersion.workload,
                container: "",
                text: validVersion.message,
              });
            }
          }

          // deployment level
          if (deployment.status?.message) {
            if (messages.some((m) => m.type === "deployment")) {
              continue;
            }
            if (deployment.status.message.includes(SUSPEND_MESSAGE) || deployment.status.message.includes(MAXSCALE_ZERO_MESSAGE)) {
              continue;
            }
            messages.push({
              type: "deployment",
              location: deployment.name,
              version: undefined,
              container: "",
              text: deployment.status.message,
            });
            continue;
          }
        }

        // Determine if workload should be considered healthy
        // If all deployments are maxScale=0, treat as healthy (intentionally deactivated)
        const allDeploymentsAreMaxScaleZero = deployments.length > 0 && maxScaleZeroDeploymentCount === deployments.length;
        // If some but not all deployments are maxScale=0, recalculate health based on active deployments
        const activeDeploymentCount = deployments.length - maxScaleZeroDeploymentCount;
        // Workload is ready if:
        // 1. All deployments are maxScale=0 (intentionally deactivated), OR
        // 2. The health check says it's ready
        const adjustedIsReady = allDeploymentsAreMaxScaleZero || (health.isReady && !health.syncFailed);

        const processedWorkload: ProcessedWorkload = {
          item: workload,
          selfLink: selfLink,
          deployments: deployments,
          version: workload.version,
          expectedDeploymentVersion: expectedDeploymentVersion,
          readyReplicaCount: readyReplicaCount,
          isCron: workload.spec.type === "cron",
          status: {
            isReady: adjustedIsReady,
            isLatestReady: allDeploymentsAreMaxScaleZero || (health.isLatestReady && !health.syncFailed),
            syncFailed: health.syncFailed,
            suspended: suspendType === "suspended",
            partiallySuspended: suspendType === "partiallySuspended",
          },
          messages: messages,
        };

        if (processedWorkload.status.syncFailed) {
          processedGvc.workloads.syncFailed.push(processedWorkload);
          continue;
        }
        if (processedWorkload.status.suspended) {
          processedGvc.workloads.suspended.push(processedWorkload);
          continue;
        }
        if (processedWorkload.status.isReady && !processedWorkload.status.isLatestReady) {
          processedGvc.workloads.notLatest.push(processedWorkload);
          continue;
        }
        if (!processedWorkload.status.isReady) {
          processedGvc.workloads.notReady.push(processedWorkload);
          continue;
        }
        if (processedWorkload.status.partiallySuspended) {
          processedGvc.workloads.partiallySuspended.push(processedWorkload);
          continue;
        }
        processedGvc.workloads.ready.push(processedWorkload);

        // TOOD domain - ignore usedByGvc message, consider like ready
        // TOOD quota - only error if 0
        // TOOD agents - ignore if never was activated
        // TODO can add double direction check for ipsets with gvc/workload
        // TODO can add org cost for the past month, current month actual and current month projection

        // continue here if suspended

        // const maybeMessages: (string | undefined)[] = [
        //   ...deployments.flatMap((d) => d.status?.message),
        //   ...deployments.flatMap((d) => Object.values(d.status?.versions || {}).flatMap((v) => v.message)),
        //   ...deployments.flatMap((d) =>
        //     Object.values(d.status?.versions || {}).flatMap((v) =>
        //       Object.values(v.containers || {}).flatMap((c) => c.message),
        //     ),
        //   ),
        // ];
        // const messages: string[] = maybeMessages.filter(Boolean) as string[];
      }

      processedGvcs.push(processedGvc);
    }
    return processedGvcs;
  },
);
